import { apiSlice } from "../apiSlice";
import {
  Application,
  ApplicationList,
  ApplicationPostRequest,
  ApplicationEligibility,
} from "./applicationModels";

interface ApplicationTupleResponse {
  item1: Application;
  item2: string;
}

export const applicationApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getApplicationList: build.query<ApplicationList, void>({
      query: () => ({
        url: "/application/list",
        method: "GET",
      }),

      transformResponse: (
        response: ApplicationTupleResponse[],
      ): ApplicationList => {
        return response.map((res: ApplicationTupleResponse) => {
          const application = res.item1;
          const followUpInstruction = res.item2;

          return {
            ...application,
            followUpInstruction: followUpInstruction,
          };
        });
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Application", id }) as const),
              { type: "Application", id: "LIST" },
            ]
          : [{ type: "Application", id: "LIST" }],
    }),
    getApplication: build.query<Application, string>({
      query: (code) => `/application/${code}`,
      providesTags: (result, error, code) => [
        { type: "Application", id: code },
      ],
    }),
    getApplicationEligibility: build.query<ApplicationEligibility, string>({
      query: (applicationType) =>
        `/application/eligibilityStatus/${applicationType}`,
      providesTags: (result, error, code) => [
        { type: "Application", id: code },
      ],
    }),
    getTransformedApplication: build.query<Application, string>({
      query: (code) => `/application/${code}`,
      transformResponse: (response: ApplicationTupleResponse) => {
        let application = response.item1;

        if (application?.applicationDataJson) {
          try {
            const parsedData = JSON.parse(application.applicationDataJson);

            return {
              ...application,
              ...parsedData,
              userDataChanged: application.userDataChanged,
              statusTransitionEvent: application.statusTransitionEvent,
              verificationStatusChangeEvent: application.verificationStatusChangeEvent,
              internalStatus: application.internalStatus,
              verificationStatus: application.verificationStatus,
              firstName: application.firstName ?? parsedData.firstName,
              lastName: application.lastName ?? parsedData.lastName,
              middleName: application.middleName ?? parsedData.middleName,
              email: application.email ?? parsedData.email,
              templateVersion:
                application.templateVersion ?? parsedData.templateVersion,
              streetAddressLine1:
                application.streetAddressLine1 ?? parsedData.streetAddressLine1,
              streetAddressLine2:
                application.streetAddressLine2 ?? parsedData.streetAddressLine2,
              locality: application.locality ?? parsedData.locality,
              province: application.province ?? parsedData.province,
              postalCode: application.postalCode ?? parsedData.postalCode,
              phone: application.phone ?? parsedData.phone,
              phoneExtension:
                application.phoneExtension ?? parsedData.phoneExtension,
              mobile: application.mobile ?? parsedData.mobile,
              followUpInstruction: response.item2,
              applicationDataJson: null,
              submittedOn: application.submittedOn ?? parsedData.submittedOn,
              updatedTime: application.updatedTime ?? parsedData.updatedTime,
              internalPortal:
                application.internalPortal ?? parsedData.internalPortal,
            };
          } catch (error) {
            console.warn("Failed to parse applicationDataJson", error);
          }
        }
        if (!application) {
          return {
            followUpInstruction: response.item2,
            applicationDataJson: null,
          };
        }

        return {
          ...application,
          followUpInstruction: response.item2,
          applicationDataJson: null,
        };
      },
      providesTags: (result, error, code) => [
        { type: "Application", id: code },
      ],
    }),
    saveDraftApplication: build.mutation<
      Application,
      Partial<ApplicationPostRequest>
    >({
      query: (body) => ({
        url: `/application/saveApplicationAsDraft`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Application", id: "LIST" }],
    }),
    applyValueMappings: build.mutation<string, string>({
      query: (application) => ({
        url: `/application/applyValueMappings`,
        method: "POST",
        body: { application },
      }),
    }),
    submitApplication: build.mutation<
      Application,
      Partial<ApplicationPostRequest>
    >({
      query: (body) => ({
        url: `/application/submitApplication`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Application", id: "LIST" }],
    }),
  }),
});

export const {
  useGetApplicationQuery,
  useGetTransformedApplicationQuery,
  useGetApplicationEligibilityQuery,
  useGetApplicationListQuery,
  useLazyGetApplicationListQuery,
  useSaveDraftApplicationMutation,
  useSubmitApplicationMutation,
  useApplyValueMappingsMutation,
} = applicationApi;
